/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { BarChartProps, BarItemData } from './barChart.types';
import { getBarGapRatio } from './utils/barSeriesUtils';
import { useUser } from '../hooks/UNSAFE_useUser';
import { BarGroup } from './BarGroup';
import { findNearestDataPoint } from '../utils/PRIVATE_chartUtils/layoutUtils';
import {
  getBarCenterCoordinate,
  getBarPosition,
  getSeriesOffsetAndWidth
} from './utils/barLayoutUtils';
import { getBarChartNavUtil } from './utils/barNavUtils';
import { Chart } from '../PRIVATE_Chart';
import { TimeAxisProps, Scale } from '../utils/UNSAFE_visTypes/chart';
import { getItemColor, getMarkerType } from '../utils/PRIVATE_chartUtils/dataStyleUtils';

/* bar chart start and end axis offset */
const AXIS_OFFSET = 0.5;

/**
 * A Bar chart displays information graphically using rectangular bars with heights or lengths proportional to the values that they represent,
 * making relationships among the data easier to understand.
 */
export function BarChart<K extends string | number, D extends BarItemData<K>>({
  width,
  height,
  selectedIds = [],
  hiddenIds = [],
  highlightedIds,
  hideAndShowBehavior = 'none',
  orientation = 'vertical',
  yAxis,
  series,
  groups,
  isStacked = false,
  getDataItem,
  selectionMode = 'none',
  drilling = 'off',
  dataItemGaps = 0.5,
  ...props
}: BarChartProps<K, D>) {
  const { direction } = useUser();
  const isRtl = direction === 'rtl';
  const hiddenSet = new Set(hiddenIds);
  const isHoriz = orientation === 'horizontal';
  const timeAxisType = (props.xAxis as TimeAxisProps)?.timeAxisType;
  const selectedSet = new Set(selectedIds);
  const highlightedSet = new Set(highlightedIds);
  const getGapRatio = (startIndex: number, endIndex: number) => {
    return getBarGapRatio(isStacked, startIndex, endIndex);
  };
  const getNextChartItemFunc = (startIndex: number, endIndex: number) => {
    return getBarChartNavUtil(getDataItem, series.length, startIndex, endIndex, isStacked, isRtl);
  };
  const getDataItemPos = (xScale: Scale, yScale: Scale, groupWidth: number, gapRatio: number) => {
    return getBarPosition(
      series,
      groups,
      getDataItem,
      xScale,
      yScale,
      hiddenSet,
      dataItemGaps,
      groupWidth,
      gapRatio,
      yAxis?.scale === 'log',
      isStacked,
      isRtl,
      isHoriz,
      timeAxisType
    );
  };

  const findNearest = (xScale: Scale, yScale: Scale, groupWidth: number, gapRatio: number) => {
    const getSeriesDataInfo = getSeriesOffsetAndWidth(
      groupWidth,
      gapRatio,
      series.length,
      dataItemGaps,
      isStacked,
      isRtl
    );
    return findNearestDataPoint(
      series,
      groups,
      xScale,
      yScale,
      getDataItem,
      isStacked,
      getSeriesDataInfo,
      getBarCenterCoordinate,
      yAxis?.scale === 'log',
      dataItemGaps,
      hiddenSet,
      timeAxisType
    );
  };

  const getMarkerColorAndType = (seriesIndex: number, groupIndex: number) => {
    return {
      color: getItemColor<K, D>(seriesIndex, groupIndex, series, getDataItem),
      type: getMarkerType<K>(seriesIndex, groupIndex, 'bar', getDataItem)
    };
  };

  return (
    <Chart
      width={width}
      height={height}
      selectedIds={selectedSet}
      hiddenIds={hiddenIds}
      hideAndShowBehavior={hideAndShowBehavior}
      orientation={orientation}
      yAxis={yAxis}
      series={series}
      groups={groups}
      isStacked={isStacked}
      getDataItem={getDataItem}
      selectionMode={selectionMode}
      drilling={drilling}
      renderGridLinesInFront={false}
      findNearest={findNearest}
      getDataItemPos={getDataItemPos}
      getNavUtil={getNextChartItemFunc}
      getGapRatio={getGapRatio}
      getMarkerInfo={getMarkerColorAndType}
      isRtl={isRtl}
      offset={AXIS_OFFSET}
      {...props}>
      {({
        xStartIndex,
        xEndIndex,
        xScale,
        yScale,
        gapRatio,
        activeId,
        focusedItemInfo,
        hoveredItemInfo,
        groupWidth,
        isPointInsideMarquee,
        getTextDimensions,
        defaultFontSize,
        colors
      }) => {
        return (
          <>
            {groups.slice(xStartIndex, xEndIndex + 1).map((_, index) => (
              <BarGroup
                yScale={yScale}
                xScale={xScale}
                groupIndex={index + xStartIndex}
                selectedIds={selectedSet}
                hiddenIds={hiddenSet}
                highlightedIds={highlightedIds ? highlightedSet : highlightedIds}
                isDrillEnabled={drilling === 'on'}
                series={series}
                barGapRatio={gapRatio}
                dataItemGaps={dataItemGaps}
                isPointInsideMarquee={isPointInsideMarquee}
                isStacked={isStacked}
                groupWidth={groupWidth}
                group={groups[index]}
                timeAxisType={(props.xAxis as TimeAxisProps)?.timeAxisType}
                colors={colors}
                activeId={activeId}
                isLog={yAxis?.scale === 'log'}
                orientation={orientation}
                getDataItem={getDataItem}
                isSelectionEnabled={selectionMode != 'none'}
                isRtl={isRtl}
                focusedItemInfo={focusedItemInfo}
                hoveredItemInfo={hoveredItemInfo}
                getTextDimensions={getTextDimensions}
                defaultFontSize={defaultFontSize}
                getStackLabel={props.getStackLabel}
              />
            ))}
          </>
        );
      }}
    </Chart>
  );
}
